import { buildClusterFaqJsonLdAsync } from "@/lib/clusterFaq";
import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import { ServicePageEnhancer } from "@/components/service/ServicePageEnhancer";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import AnswerBoxTop from "@/components/AnswerBoxTop";
import { HubCountryLinks } from "@/components/HubCountryLinks";
// EN mirror of /apostille — hub for Hague Apostille & consular legalization
import { createFileRoute, Link } from "@tanstack/react-router";
import { speakableSchema } from "@/lib/aeo";
import { Stamp, Globe2, ShieldCheck, Clock3 } from "lucide-react";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { APOSTILLE_COUNTRIES } from "@/data/apostille-countries";
import { RelatedComparisons } from "@/components/RelatedComparisons";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "Apostille & Legalization Bangkok — 51 Countries | NYC Language Institute";
const DESC =
  "Hague Apostille and consular legalization for Thai documents to 51 destination countries. Talk to a specialist today; no obligation, fast reply.";

export const Route = createFileRoute("/en/apostille/")({
  loader: async () => ({ clusterFaq: await buildClusterFaqJsonLdAsync("apostille", "en", "https://nyclanguageinstitute.com/en/apostille") }),
  head: ({ loaderData }: any) => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/apostille", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/apostille", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/apostille", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/apostille", "en")},
      { property: "og:url", content: `${SITE}/en/apostille` },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/apostille` },
      ...hreflangLinks("/apostille", "/en/apostille"),
    ],
    scripts: [speakableSchema(), {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Apostille & Legalization",
        provider: { "@type": "LegalService", name: "NYC Legal & Notary Service Co., Ltd.", url: SITE },
        areaServed: "TH",
        inLanguage: "en",
        serviceType: "Document Apostille and Consular Legalization",
        offers: { "@type": "Offer", priceCurrency: "THB", price: "3500" },
      }),
    }, {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: "Get a Thai document apostilled or legalized",
        inLanguage: "en",
        totalTime: "P7D",
        estimatedCost: { "@type": "MonetaryAmount", currency: "THB", value: "3500" },
        step: [
          { "@type": "HowToStep", position: 1, name: "Obtain the original Thai document", text: "Order an up-to-date original from the issuing Thai authority (amphur, university, hospital).", url: `${SITE}/en/translate` },
          { "@type": "HowToStep", position: 2, name: "Certified translation", text: "We translate it into English (or destination language) with a sworn translator's seal.", url: `${SITE}/en/translate` },
          { "@type": "HowToStep", position: 3, name: "MFA legalization", text: "The Department of Consular Affairs authenticates the translation. 1–3 working days.", url: `${SITE}/en/apostille` },
          { "@type": "HowToStep", position: 4, name: "Apostille or embassy legalization", text: "For Hague members MFA issues the Apostille. For non-Hague (China, UAE, Vietnam) we also take it to the destination embassy.", url: `${SITE}/en/apostille` },
          { "@type": "HowToStep", position: 5, name: "Delivery", text: "Original returned by hand in Thailand or shipped overseas via DHL/EMS." },
        ],
      }),
    },
        ...(loaderData?.clusterFaq ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.clusterFaq) }] : []),
      ],
  }),
  component: EnApostilleHub,
});

function EnApostilleHub() {
  const hague = APOSTILLE_COUNTRIES.filter((c) => c.isHague);
  const legal = APOSTILLE_COUNTRIES.filter((c) => !c.isHague);
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep px-4 py-16 text-white">
        <div className="mx-auto max-w-5xl">
          <Link to="/en" className="text-xs text-amber-300 hover:underline">← EN home</Link>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            Apostille & Legalization — <span className="text-amber-300">{APOSTILLE_COUNTRIES.length} countries</span>
          </h1>
          <p className="mt-4 max-w-2xl text-white/80">
            Thailand acceded on 30 June 2026 (in force for Thailand 28 February 2027). We handle the full chain — certified
            translation, notarisation, MFA Department of Consular Affairs, and embassy legalization where
            required. For destinations outside Hague (China, UAE, Saudi Arabia, Vietnam, Taiwan) see our{" "}
            <Link to="/en/mfa-legalisation" className="underline decoration-amber-300 underline-offset-4 hover:text-amber-200">MFA + Embassy Legalisation hub</Link>.
            One invoice, one timeline, guaranteed acceptance.
          </p>
          <div className="quick-answer mt-6 max-w-2xl rounded-2xl border border-amber-400/40 bg-amber-500/10 p-5" data-speakable="true" itemScope itemType="https://schema.org/Question">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-amber-300">AI Quick Answer</div>
            <h2 className="text-base font-bold text-white sm:text-lg" itemProp="name">How long does Apostille take in Thailand?</h2>
            <div itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
              <p className="direct-answer mt-2 text-sm leading-relaxed text-white/85" itemProp="text">Standard Apostille at MFA Department of Consular Affairs takes 3 business days. NYC Language Institute completes the full chain (translation + notarisation + MFA Apostille) in 5–7 business days from THB 3,500. Rush 48-hour service is available for urgent cases across all 125+ Hague member countries.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto grid max-w-5xl gap-5 sm:grid-cols-3">
          {[
            { icon: Stamp, t: "Apostille (Hague)", d: "Single-stamp MFA certificate. Accepted in 125+ Hague member states." },
            { icon: Globe2, t: "Consular legalization", d: "MFA + destination embassy for non-Hague countries (UAE, China, Vietnam…)." },
            { icon: ShieldCheck, t: "First-try acceptance", d: "We re-do free if the receiving authority rejects on procedural grounds." },
          ].map((c) => (
            <article key={c.t} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <c.icon className="h-7 w-7 text-amber-600" />
              <h2 className="mt-3 text-base font-bold text-navy-deep">{c.t}</h2>
              <p className="mt-2 text-sm text-slate-600">{c.d}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 px-4 py-14">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold text-navy-deep">Hague Apostille destinations ({hague.length})</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {hague.map((c) => (
              <Link key={c.slug} to="/apostille/$country" params={{ country: c.slug }}
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm hover:border-amber-400">
                <span className="text-xl">{c.flag}</span>
                <span className="font-medium text-navy-deep">{c.nameEn}</span>
              </Link>
            ))}
          </div>

          <h2 className="mt-12 text-2xl font-extrabold text-navy-deep">Consular legalization destinations ({legal.length})</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {legal.map((c) => (
              <Link key={c.slug} to="/apostille/$country" params={{ country: c.slug }}
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm hover:border-amber-400">
                <span className="text-xl">{c.flag}</span>
                <span className="font-medium text-navy-deep">{c.nameEn}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-14">
        <div className="mx-auto max-w-5xl rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-700">
          <Clock3 className="h-5 w-5 text-amber-600" />
          <p className="mt-2">
            Average turnaround 5–7 business days · Rush 48h available · Original documents returned by EMS/DHL.
            Need a quote? Visit <Link to="/en/quote" className="font-bold text-amber-700 hover:underline">/en/quote</Link>{" "}
            or message LINE <a href="https://line.me/ti/p/@nycli" className="font-bold text-amber-700 hover:underline">@nycli</a>.
          </p>
        <RelatedHubGrid locale="en" />
      </div>
      </section>
      <RelatedComparisons category="apostille" locale="en" />
          <QuotableStatsStrip cluster="apostille" locale="en" variant="light" />
          <HubCountryLinks service="apostille" lang="en" />
      <ServicePageEnhancer serviceName="Apostille" locale="en" />

      <PlanningToolsLinks locale="en" />
      </main>
  );
}
